import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, EyeIcon, FilterIcon, UserPlusIcon } from 'lucide-react';
// Mock user data
const generateMockUsers = () => {
  const roles = ['Admin', 'Manager', 'Operator', 'Viewer'];
  const statuses = ['Active', 'Inactive', 'Locked'];
  return Array.from({
    length: 20
  }, (_, i) => ({
    id: i + 1,
    name: `User ${i + 1}`,
    email: `user${i + 1}@example.com`,
    role: roles[Math.floor(Math.random() * roles.length)],
    status: statuses[Math.floor(Math.random() * statuses.length)],
    lastLogin: new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date(Date.now() - Math.floor(Math.random() * 365) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  }));
};
const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
  useEffect(() => {
    // Load mock data
    setUsers(generateMockUsers());
  }, []);
  // Filter users based on search term
  const filteredUsers = users.filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.role.toLowerCase().includes(searchTerm.toLowerCase()));
  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const openModal = (mode: 'view' | 'edit' | 'create', user?: any) => {
    setModalMode(mode);
    setSelectedUser(user || null);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  return <div className="space-y-6" data-id="element-321">
      <div className="flex justify-between items-center" data-id="element-322">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-323">
          User Management
        </h1>
        <button onClick={() => openModal('create')} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-324">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-325" />
          Add User
        </button>
      </div>
      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-326">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-327">
          <div className="flex items-center space-x-4" data-id="element-328">
            <div className="relative rounded-md shadow-sm max-w-xs" data-id="element-329">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-330">
                <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-331" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search users..." data-id="element-332" />
            </div>
            <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-333">
              <FilterIcon className="h-4 w-4 mr-1" data-id="element-334" />
              Filter
            </button>
          </div>
          <div className="flex items-center space-x-2" data-id="element-335">
            <span className="text-sm text-gray-500" data-id="element-336">
              Showing {indexOfFirstUser + 1} to{' '}
              {Math.min(indexOfLastUser, filteredUsers.length)} of{' '}
              {filteredUsers.length} users
            </span>
          </div>
        </div>
      </div>
      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-337">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-338">
          <thead className="bg-gray-50" data-id="element-339">
            <tr data-id="element-340">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-341">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-342">
                Email
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-343">
                Role
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-344">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-345">
                Last Login
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-346">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-347">
            {currentUsers.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-348">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-349">
                  <div className="flex items-center" data-id="element-350">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-semibold" data-id="element-351">
                      {user.name.charAt(0)}
                    </div>
                    <div className="ml-4" data-id="element-352">
                      <div className="text-sm font-medium text-gray-900" data-id="element-353">
                        {user.name}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-354">ID: {user.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-355">
                  <div className="text-sm text-gray-900" data-id="element-356">{user.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-357">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${user.role === 'Admin' ? 'bg-purple-100 text-purple-800' : user.role === 'Manager' ? 'bg-blue-100 text-blue-800' : user.role === 'Operator' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-358">
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-359">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${user.status === 'Active' ? 'bg-green-100 text-green-800' : user.status === 'Inactive' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}`} data-id="element-360">
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-361">
                  {user.lastLogin}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-362">
                  <div className="flex justify-end space-x-2" data-id="element-363">
                    <button onClick={() => openModal('view', user)} className="text-gray-400 hover:text-gray-500" data-id="element-364">
                      <EyeIcon className="h-4 w-4" data-id="element-365" />
                    </button>
                    <button onClick={() => openModal('edit', user)} className="text-blue-400 hover:text-blue-500" data-id="element-366">
                      <EditIcon className="h-4 w-4" data-id="element-367" />
                    </button>
                    <button className="text-red-400 hover:text-red-500" data-id="element-368">
                      <TrashIcon className="h-4 w-4" data-id="element-369" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-370">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-371">
            <div data-id="element-372">
              <p className="text-sm text-gray-700" data-id="element-373">
                Showing{' '}
                <span className="font-medium" data-id="element-374">{indexOfFirstUser + 1}</span> to{' '}
                <span className="font-medium" data-id="element-375">
                  {Math.min(indexOfLastUser, filteredUsers.length)}
                </span>{' '}
                of <span className="font-medium" data-id="element-376">{filteredUsers.length}</span>{' '}
                results
              </p>
            </div>
            <div data-id="element-377">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-378">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50" data-id="element-379">
                  <span className="sr-only" data-id="element-380">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-381">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-382" />
                  </svg>
                </button>
                {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => handlePageChange(page)} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${page === currentPage ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-383">
                    {page}
                  </button>)}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50" data-id="element-384">
                  <span className="sr-only" data-id="element-385">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-386">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" data-id="element-387" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
      {/* User Modal */}
      {isModalOpen && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-388">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-389">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-390">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-391"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-392">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-393">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-394">
                <div className="sm:flex sm:items-start" data-id="element-395">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-396">
                    <UserPlusIcon className="h-6 w-6 text-blue-600" data-id="element-397" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-398">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-399">
                      {modalMode === 'view' ? 'User Details' : modalMode === 'edit' ? 'Edit User' : 'Create User'}
                    </h3>
                    <div className="mt-4 space-y-4" data-id="element-400">
                      <div data-id="element-401">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-402">
                          Name
                        </label>
                        <input type="text" id="name" value={selectedUser?.name || ''} readOnly={modalMode === 'view'} className={`mt-1 block w-full shadow-sm sm:text-sm rounded-md ${modalMode === 'view' ? 'bg-gray-50 border-gray-300' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-403" />
                      </div>
                      <div data-id="element-404">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-405">
                          Email
                        </label>
                        <input type="email" id="email" value={selectedUser?.email || ''} readOnly={modalMode === 'view'} className={`mt-1 block w-full shadow-sm sm:text-sm rounded-md ${modalMode === 'view' ? 'bg-gray-50 border-gray-300' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-406" />
                      </div>
                      <div data-id="element-407">
                        <label htmlFor="role" className="block text-sm font-medium text-gray-700" data-id="element-408">
                          Role
                        </label>
                        {modalMode === 'view' ? <input type="text" id="role" value={selectedUser?.role || ''} readOnly className="mt-1 block w-full shadow-sm sm:text-sm rounded-md bg-gray-50 border-gray-300" data-id="element-409" /> : <select id="role" value={selectedUser?.role || 'Viewer'} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-410">
                            <option value="Admin" data-id="element-411">Admin</option>
                            <option value="Manager" data-id="element-412">Manager</option>
                            <option value="Operator" data-id="element-413">Operator</option>
                            <option value="Viewer" data-id="element-414">Viewer</option>
                          </select>}
                      </div>
                      <div data-id="element-415">
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-416">
                          Status
                        </label>
                        {modalMode === 'view' ? <input type="text" id="status" value={selectedUser?.status || ''} readOnly className="mt-1 block w-full shadow-sm sm:text-sm rounded-md bg-gray-50 border-gray-300" data-id="element-417" /> : <select id="status" value={selectedUser?.status || 'Active'} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-418">
                            <option value="Active" data-id="element-419">Active</option>
                            <option value="Inactive" data-id="element-420">Inactive</option>
                            <option value="Locked" data-id="element-421">Locked</option>
                          </select>}
                      </div>
                      {modalMode !== 'create' && <div data-id="element-422">
                          <label htmlFor="lastLogin" className="block text-sm font-medium text-gray-700" data-id="element-423">
                            Last Login
                          </label>
                          <input type="text" id="lastLogin" value={selectedUser?.lastLogin || ''} readOnly className="mt-1 block w-full shadow-sm sm:text-sm rounded-md bg-gray-50 border-gray-300" data-id="element-424" />
                        </div>}
                      {modalMode === 'create' && <>
                          <div data-id="element-425">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-426">
                              Password
                            </label>
                            <input type="password" id="password" className="mt-1 block w-full shadow-sm sm:text-sm rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500" data-id="element-427" />
                          </div>
                          <div data-id="element-428">
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700" data-id="element-429">
                              Confirm Password
                            </label>
                            <input type="password" id="confirmPassword" className="mt-1 block w-full shadow-sm sm:text-sm rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500" data-id="element-430" />
                          </div>
                        </>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-431">
                {modalMode !== 'view' && <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-432">
                    {modalMode === 'create' ? 'Create' : 'Save Changes'}
                  </button>}
                <button type="button" onClick={closeModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-433">
                  {modalMode === 'view' ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default UserManagement;